import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Modal, TextField, Typography, DragAndDrop } from '@ann/web-components';
import { observer } from 'mobx-react';
import { useStore } from '../../../../../../../hooks';
import { useToast } from '../../../../../../../components/toast';
import { useMediaQueries } from '../../../../../../../utils/mediaQueries';
import { ErrorMessage, ModalBodyContent, UploadPhotoContainer } from './EditWorkspaceModal.styled';

const EditWorkspaceModal = ({
  size = "medium",
  isOpen,
  onClose,
  disabled = false,
  workspace = null,
  workspaceId = null
}) => {
  const { workspaceStore } = useStore();
  const toast = useToast();
  const { isIpadAirPortrait, isIpadProPortrait } = useMediaQueries();
  const [workspaceName, setWorkspaceName] = useState('');
  const [address, setAddress] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const fileInputRef = useRef(null);
  
  // Calculate modal width: 90% for iPad portrait, 900px otherwise
  const modalWidth = useMemo(() => {
    if (isIpadAirPortrait || isIpadProPortrait) {
      return '90%';
    }
    return '900px';
  }, [isIpadAirPortrait, isIpadProPortrait]);
  
  // Get workspace data with fallbacks (defensive programming: handle both camelCase and snake_case)
  // Priority: workspaceName/workspace_name (new) > tenantName/tenant_name (legacy) > name (generic) > fallback
  const workspaceNameFromProps = workspace?.workspaceName || workspace?.workspace_name || workspace?.tenantName || workspace?.tenant_name || workspace?.name || '';
  const addressFromProps = workspace?.address || '';
  const contactEmailFromProps = workspace?.contact_email || workspace?.contactEmail || workspace?.ownerEmail || workspace?.owner_email || '';
  // Use workspaceId from URL as fallback if workspace object doesn't have tenant_uuid
  const tenantUuid = workspace?.tenant_uuid || workspace?.tenantUuid || workspace?.tenant_id || workspace?.tenantId || workspaceId;
  
  // Initialize form fields when modal opens or workspace data changes
  useEffect(() => {
    if (isOpen && workspace) {
      setWorkspaceName(workspaceNameFromProps);
      setAddress(addressFromProps);
      // Reset image preview and file when opening modal
      setImagePreview(null);
      setLogoFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen, workspace, workspaceNameFromProps, addressFromProps]);
  
  // Store contact_email from workspace (read-only, not editable in this modal)
  const contactEmail = contactEmailFromProps;
  
  const isFieldEmpty = !workspaceName.trim();
  const showNameError = isFieldEmpty;
  const isFormValid = !isFieldEmpty;
  const isUpdating = workspaceStore.isLoading;
  const previousLoadingRef = useRef(false);

  // Show toast notifications based on store state
  useEffect(() => {
    // Only process when loading transitions from true to false
    if (previousLoadingRef.current && !isUpdating && isOpen) {
      // Check for success first
      if (workspaceStore.selectedWorkspace && !workspaceStore.error) {
        // Success: workspace was updated
        // Priority: workspaceName/workspace_name (new) > tenantName/tenant_name (legacy) > name (generic) > fallback
        const workspaceName = workspaceStore.selectedWorkspace.workspaceName || workspaceStore.selectedWorkspace.workspace_name || workspaceStore.selectedWorkspace.tenantName || workspaceStore.selectedWorkspace.tenant_name || workspaceStore.selectedWorkspace.name || 'Workspace';
        toast.success({
          title: 'Workspace updated',
          message: `Your changes have been applied.`,
          canClose: true
        });
        // Reset form and close modal
        setWorkspaceName('');
        setAddress('');
        setImagePreview(null);
        setLogoFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        workspaceStore.clearError();
        onClose();
      } 
      // Check for error
      else if (workspaceStore.error) {
        // Error: show error toast
        const errorMessage = workspaceStore.errorMessage || 'An error occurred while updating workspace';
        toast.error({
          message: errorMessage,
          canClose: true
        });
      }
    }
    previousLoadingRef.current = isUpdating;
  }, [isUpdating, workspaceStore.selectedWorkspace, workspaceStore.error, workspaceStore.errorMessage, isOpen, toast, onClose]);

  const handleSave = async () => {
    console.log('[EditWorkspaceModal] handleSave called');
    console.log('[EditWorkspaceModal] isFormValid:', isFormValid, '| tenantUuid:', tenantUuid, '| contactEmail:', contactEmail);
    console.log('[EditWorkspaceModal] workspace prop:', workspace);
    
    if (!isFormValid || !tenantUuid) {
      console.log('[EditWorkspaceModal] Validation failed: isFormValid=', isFormValid, 'tenantUuid=', tenantUuid);
      toast.error({
        message: `Validation failed: ${!isFormValid ? 'Form invalid' : 'Missing tenant UUID'}`,
        canClose: true
      });
      return;
    }

    try {
      if (!contactEmail) {
        toast.error({
          message: 'Contact email is required to update workspace',
          canClose: true
        });
        return;
      }

      const workspaceData = {
        tenant_uuid: tenantUuid,
        tenant_name: workspaceName.trim(),
        contact_email: contactEmail,
        address: address.trim() || '',
      };

      console.log('[EditWorkspaceModal] Sending workspaceData:', workspaceData);
      await workspaceStore.updateWorkspace(workspaceData);
      
      // Verify update was successful before uploading photo
      const updateWasSuccessful = !workspaceStore.error && workspaceStore.selectedWorkspace;
      
      // Upload photo after workspace update (only if update was successful and file is selected)
      if (logoFile && tenantUuid && updateWasSuccessful) {
        try {
          await workspaceStore.uploadTenantPhoto(tenantUuid, logoFile);
          // No toast on success - only the update success toast will show
        } catch (photoError) {
          console.error('Error uploading tenant photo:', photoError);
          toast.error({
            message: 'Failed to update photo. Please try again later.',
            canClose: true
          });
        }
      }
      
      // Toast notifications and modal close are handled by useEffect
      
    } catch (error) {
      // Error handling is done in useEffect based on store state
      console.error('Error updating workspace:', error);
    }
  };

  const handleClose = () => {
    setWorkspaceName('');
    setAddress('');
    setImagePreview(null);
    setLogoFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    workspaceStore.clearError();
    onClose();
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    const file = e.dataTransfer.files[0];
    if (file) {
      setLogoFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width={modalWidth} size={size}>
      <Modal.Header>
        <Modal.Header.Title>
          Edit Workspace
        </Modal.Header.Title>
        <Modal.Header.Description>
          <Typography category="body" size="medium" weight="regular" color="body">
            Update organization, division, or workspace information.
          </Typography>
        </Modal.Header.Description>
      </Modal.Header>
      <Modal.Body>
        <ModalBodyContent>
          <TextField 
          label="Name"
          size="small" 
          placeholder="eg: Atlas Financial Group"
          value={workspaceName}
          onChange={(value) => setWorkspaceName(value)}
          required={true}
          />
          {showNameError && (
          <ErrorMessage>Workspace name is required</ErrorMessage>
          )}

          <TextField 
            label="Address / Location (Optional)"
            size="small" 
            placeholder="Enter full address"
            value={address}
            onChange={(value) => setAddress(value)}
          />

          <UploadPhotoContainer>
            <Typography category="label" size="small" weight="bold">
              Logo (Optional)
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.svg"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <DragAndDrop
              className="drag-and-drop"
              uploadType="images"
              acceptedFileTypes={['.png', '.jpg', '.jpeg', '.svg']}
              onUploadClick={handleUploadClick}
              onDrop={handleDrop}
              width="100%"
              imagePreview={imagePreview}
            />
          </UploadPhotoContainer>

        </ModalBodyContent>
      </Modal.Body>
      <Modal.Actions align="end">
        <Modal.Button
          label="Cancel"
          onClick={handleClose}
          type="secondary"
          disabled={isUpdating}
        />
        <Modal.Button
          label={isUpdating ? "Updating..." : "Update"}
          onClick={handleSave}
          disabled={disabled || isUpdating || !isFormValid}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default observer(EditWorkspaceModal);

